import { useCallback, useEffect, useState, type FormEvent } from "react";

import { authFetch } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { IconPlus } from "@/components/Icon";
import { Pill, statusToTone } from "@/components/Pill";
import { useStore } from "@/state/store";

interface Market {
  id: string;
  slug: string;
  title: string;
  status: string;
  close_at: string;
  resolve_after: string;
  resolution_source: string;
}

interface MarketListResponse {
  items: Market[];
  next_cursor?: string | null;
}

export function MarketsPage() {
  const auth = useAuth();
  const showToast = useStore((s) => s.showToast);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [actionRowId, setActionRowId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("/v1/markets?limit=100");
      if (!res.ok) throw new Error(`markets ${res.status}`);
      const data = (await res.json()) as MarketListResponse;
      setMarkets(data.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setMarkets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function runAction(market: Market, kind: "open" | "close") {
    setActionRowId(market.id);
    try {
      const res = await authFetch(`/v1/admin/markets/${market.id}/${kind}`, {
        method: "POST",
      });
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `${kind} fallita (${res.status})`);
      }
      showToast({
        kind: "success",
        message: `Market ${market.slug} ${kind === "open" ? "aperto" : "chiuso"}.`,
      });
      await load();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "azione fallita",
      });
    } finally {
      setActionRowId(null);
    }
  }

  const columns: DataTableColumn<Market>[] = [
    {
      key: "slug",
      header: "Slug",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-text-2">{m.slug}</span>
      ),
      sortValue: (m) => m.slug,
    },
    {
      key: "title",
      header: "Titolo",
      cell: (m) => <span className="text-text">{m.title}</span>,
      sortValue: (m) => m.title.toLowerCase(),
      className: "max-w-[420px]",
    },
    {
      key: "status",
      header: "Status",
      cell: (m) => <Pill tone={statusToTone(m.status)}>{m.status}</Pill>,
      sortValue: (m) => m.status,
    },
    {
      key: "close_at",
      header: "Close at",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {formatDate(m.close_at)}
        </span>
      ),
      sortValue: (m) => new Date(m.close_at),
    },
    {
      key: "resolve_after",
      header: "Resolve after",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-muted">
          {formatDate(m.resolve_after)}
        </span>
      ),
      sortValue: (m) => new Date(m.resolve_after),
    },
    {
      key: "oracle",
      header: "Oracle",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {m.resolution_source}
        </span>
      ),
      sortValue: (m) => m.resolution_source,
    },
    {
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (m) => (
        <div className="inline-flex gap-2 justify-end">
          {m.status === "created" && (
            <button
              type="button"
              className="btn-secondary py-1 px-3 text-[11.5px]"
              onClick={() => runAction(m, "open")}
              disabled={actionRowId === m.id}
            >
              Apri
            </button>
          )}
          {m.status === "open" && (
            <button
              type="button"
              className="btn-secondary py-1 px-3 text-[11.5px]"
              onClick={() => runAction(m, "close")}
              disabled={actionRowId === m.id}
            >
              Chiudi
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold">Markets</h1>
          <p className="text-[13px] text-muted mt-1">
            Crea, apri e chiudi i mercati. Le risoluzioni sono gestite nella
            pagina dedicata.
          </p>
        </div>
        {auth.role === "admin" && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            <IconPlus size={14} />
            Crea market
          </button>
        )}
      </header>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento markets fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={markets}
          columns={columns}
          rowKey={(m) => m.id}
          pageSize={25}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              Nessun market disponibile.
            </div>
          }
        />
      )}

      {createOpen && (
        <CreateMarketModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
            showToast({ kind: "success", message: "Market creato." });
          }}
        />
      )}
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Modal minimale per la creazione di un market. I campi rispecchiano i nomi
 *  attesi dal backend (`POST /v1/admin/markets`). Validazione client-side
 *  light: il vero check sta nel backend. */
function CreateMarketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [closeAt, setCloseAt] = useState("");
  const [resolveAfter, setResolveAfter] = useState("");
  const [resolutionSource, setResolutionSource] = useState("uma");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await authFetch("/v1/admin/markets", {
        method: "POST",
        body: JSON.stringify({
          slug,
          title,
          close_at: new Date(closeAt).toISOString(),
          resolve_after: new Date(resolveAfter).toISOString(),
          resolution_source: resolutionSource,
        }),
      });
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Creazione fallita (${res.status})`);
      }
      onCreated();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "creazione fallita",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Nuovo market</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Slug</span>
            <input
              className="input"
              required
              pattern="[a-z0-9-]+"
              placeholder="es-presidential-2028"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={submitting}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Titolo</span>
            <input
              className="input"
              required
              placeholder="Will X happen by Y?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Close at</span>
            <input
              className="input"
              type="datetime-local"
              required
              value={closeAt}
              onChange={(e) => setCloseAt(e.target.value)}
              disabled={submitting}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Resolve after</span>
            <input
              className="input"
              type="datetime-local"
              required
              value={resolveAfter}
              onChange={(e) => setResolveAfter(e.target.value)}
              disabled={submitting}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Resolution source</span>
            <select
              className="input"
              value={resolutionSource}
              onChange={(e) => setResolutionSource(e.target.value)}
              disabled={submitting}
            >
              {/* ADR-007: `manual` è vietato in creazione — il backend
                  risponde 400. Solo oracoli on-chain. */}
              <option value="uma">UMA</option>
              <option value="chainlink">Chainlink</option>
              <option value="sportradar">Sportradar</option>
            </select>
          </label>

          <div className="flex gap-2 justify-end mt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Annulla
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Creazione…" : "Crea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
